import {DialogContent} from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import {createStyles, makeStyles} from '@material-ui/core/styles'
import {checkAvailabilityNormApi, ZScore} from 'api/analysisApi'
import CancelButton from 'components/atoms/Buttons/CancelButton'
import ConfirmButton from 'components/atoms/Buttons/ConfirmButton'
import useEegIndividualNormDialog from 'features/analysis/useEegIndividualNormDialog'
import useEegIndividualSearch from 'features/analysis/useEegIndividualSearch'
import useFailureModal from 'features/modal/useFailureModal'
import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

type ZScoreAvailableMap = Record<ZScore, boolean>

const DefaultZScoreAvailableMap: ZScoreAvailableMap = {
  1.96: true,
  2.58: true,
  3: true,
}

const styles = () =>
  createStyles({
    inputWrapper: {
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      marginBottom: 15,
    },
    inputLabel: {
      fontSize: '1.0rem',
      fontWeight: 'normal',
      textAlign: 'left',
      margin: '5px 2px',
      minWidth: 120,
      maxWidth: 100,
    },
    buttonsContainer: {
      padding: 20,
      backgroundColor: '#f4f4f4',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 10,
      '& .MuiFormLabel-root': {
        marginBottom: 10,
      },
      '& .MuiFormControlLabel-root': {
        minWidth: 250,
      },
      '& button': {
        minWidth: 136,
      },
    },
    radioWrap: {
      display: 'flex',
      flexDirection: 'row',
      alignContent: 'center',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& label > span': {
        marginRight: 70,
        color: '#555555',
        fontSize: 13,
      },
      '& label > span > span > div > .MuiSvgIcon-fontSizeSmall': {
        fontSize: 17,
      },
    },
    radioText: {
      color: '#555555',
      fontSize: 13,
    },
    line: {
      color: '#fafafa',
      backgroundColor: '#fafafa',
      margin: '5px 0',
    },
  })
const useStyles = makeStyles(styles)

function NormRequestDialog() {
  const {t} = useTranslation()
  const classes = useStyles()

  const {open, selectedId, onConfirm, onClose} = useEegIndividualNormDialog()
  const {onOpen: onFailureModalOpen} = useFailureModal()
  const {query, onSearch} = useEegIndividualSearch()

  const [zScoreAvailableMap, setZScoreAvailableMap] =
    useState<ZScoreAvailableMap>(DefaultZScoreAvailableMap)

  const [zScore, setZScore] = useState('1.96')

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setZScore((event.target as HTMLInputElement).value)
  }

  const isCompleted = () =>
    !zScoreAvailableMap[1.96] &&
    !zScoreAvailableMap[2.58] &&
    !zScoreAvailableMap[3]

  const handleConfirm = () => {
    if (selectedId === 0) {
      return
    }

    if (zScore === '') {
      return
    }

    onConfirm({
      eegId: selectedId,
      zscore: Number.parseFloat(zScore) as ZScore,
    }).then(() => {
      setZScoreAvailableMap(DefaultZScoreAvailableMap)
      onSearch(query)
      onClose()
    })
  }

  const handleClose = () => {
    setZScoreAvailableMap(DefaultZScoreAvailableMap)
    onClose()
  }

  const handleCheckAvailability = async () => {
    const checkAvailabilityRes = await checkAvailabilityNormApi(selectedId)
    if (checkAvailabilityRes.data.available) {
      setZScoreAvailableMap((prev) => {
        const updated: ZScoreAvailableMap = {
          1.96: prev[1.96],
          2.58: prev[2.58],
          3: prev[3],
        }
        checkAvailabilityRes.data.zscores.forEach((z: ZScore) => {
          updated[z] = false
        })
        return updated
      })
      const newZScore =
        [1.96, 2.58, 3].find(
          (z) => !checkAvailabilityRes.data.zscores.includes(z as ZScore),
        ) || ''
      setZScore(String(newZScore))
    } else {
      onFailureModalOpen(String(checkAvailabilityRes.data.message))
      onClose()
    }
  }

  useEffect(() => {
    if (!open) {
      setZScoreAvailableMap(DefaultZScoreAvailableMap)
    } else {
      handleCheckAvailability()
      setZScore('1.96')
    }
  }, [open])

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle style={{color: '#333333'}}>
        {t('IZscoreAnalysisTitle')}
      </DialogTitle>
      <Divider />
      <DialogContent style={{margin: 10}}>
        <FormControl component='fieldset'>
          <RadioGroup
            aria-label='gender'
            name='zScore'
            value={zScore}
            onChange={handleChange}
          >
            <div className={classes.radioWrap}>
              <FormControlLabel
                value='1.96'
                control={<Radio color='primary' size='small' />}
                label='1.96 '
                disabled={!zScoreAvailableMap[1.96]}
              />
              {!zScoreAvailableMap[1.96] && (
                <span className={classes.radioText}>
                  {t('IHasCompletionHistory')}
                </span>
              )}
              {zScoreAvailableMap[1.96] && (
                <span className={classes.radioText}>
                  {t('IAnalysisAvailable')}
                </span>
              )}
            </div>
            <Divider className={classes.line} />
            <div className={classes.radioWrap}>
              <FormControlLabel
                value='2.58'
                control={<Radio color='primary' size='small' />}
                label='2.58'
                disabled={!zScoreAvailableMap[2.58]}
              />
              {!zScoreAvailableMap[2.58] && (
                <span className={classes.radioText}>
                  {t('IHasCompletionHistory')}
                </span>
              )}
              {zScoreAvailableMap[2.58] && (
                <span className={classes.radioText}>
                  {t('IAnalysisAvailable')}
                </span>
              )}
            </div>
            <Divider className={classes.line} />
            <div className={classes.radioWrap}>
              <FormControlLabel
                value='3'
                control={<Radio color='primary' size='small' />}
                label='3'
                disabled={!zScoreAvailableMap[3]}
              />
              {!zScoreAvailableMap[3] && (
                <span className={classes.radioText}>
                  {t('IHasCompletionHistory')}
                </span>
              )}
              {zScoreAvailableMap[3] && (
                <span className={classes.radioText}>
                  {t('IAnalysisAvailable')}
                </span>
              )}
            </div>
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <div className={classes.buttonsContainer}>
        <CancelButton onClick={handleClose} />
        <ConfirmButton onClick={handleConfirm} disabled={isCompleted()} />
      </div>
    </Dialog>
  )
}

export default NormRequestDialog
